import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, ShoppingBag, Minus, Plus, ArrowRight } from "lucide-react";
import { useCart, formatINR } from "../contexts/CartContext";
import { getSetting } from "../lib/api";

export default function CartDrawer() {
  const { items, open, subtotal, closeCart, updateQty, removeItem } = useCart();
  const navigate = useNavigate();
  const [shipCopy, setShipCopy] = useState("Shipping calculated at checkout by location");
  useEffect(() => {
    let cancelled = false;
    getSetting("shipping_copy_cart", "Shipping calculated at checkout by location").then((v) => { if (!cancelled) setShipCopy(v); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") closeCart(); };
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, closeCart]);

  const onCheckout = () => {
    closeCart();
    navigate("/checkout");
  };

  return (
    <>
      <div
        className="fixed inset-0 z-[60]"
        onClick={closeCart}
        aria-hidden="true"
        style={{
          background: "rgba(0,0,0,0.45)",
          opacity: open ? 1 : 0,
          visibility: open ? "visible" : "hidden",
          pointerEvents: open ? "auto" : "none",
          transition: `opacity 400ms ease, visibility 0s linear ${open ? "0s" : "400ms"}`,
        }}
        data-testid="cart-backdrop"
      />
      <aside
        className="top-0 right-0 h-full z-[61] w-[min(440px,94vw)] mt-glass-thick mt-rainbow-edge flex flex-col"
        style={{
          position: "fixed",
          borderTopLeftRadius: 22, borderBottomLeftRadius: 22,
          transform: open ? "translateX(0)" : "translateX(110%)",
          visibility: open ? "visible" : "hidden",
          pointerEvents: open ? "auto" : "none",
          transition: `transform 500ms cubic-bezier(0.16,1,0.3,1), visibility 0s linear ${open ? "0s" : "500ms"}`,
        }}
        role="dialog"
        aria-label="Your cart"
        data-testid="cart-drawer"
      >
        <div className="flex items-center justify-between px-7 pt-7">
          <h2 className="font-display text-2xl font-light">Your Cart</h2>
          <button onClick={closeCart} className="mt-glass rounded-full p-2" aria-label="Close cart" data-testid="cart-close">
            <X size={16} />
          </button>
        </div>
        <p className="px-7 pt-1 font-mono text-[0.65rem] tracking-mt uppercase" style={{ color: "var(--mt-text-muted)" }}>
          {items.length === 0 ? "Empty" : `${items.length} ${items.length === 1 ? "piece" : "pieces"}`}
        </p>

        <div className="flex-1 overflow-y-auto px-7 mt-6 space-y-4">
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center gap-4 pb-20" data-testid="cart-empty">
              <div className="mt-glass rounded-full p-6 opacity-60"><ShoppingBag size={36} /></div>
              <p className="font-display text-lg font-light opacity-80">Your cart is empty.</p>
              <p className="mt-italic-silver text-[0.72rem]">Every thread has a story.</p>
            </div>
          ) : items.map((it) => (
            <div key={it.id + (it.size || "")} className="mt-glass rounded-2xl p-4 flex items-center gap-4" data-testid={`cart-item-${it.id}`}>
              <div
                className="w-16 h-20 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden"
                style={{ background: "linear-gradient(140deg, #1a1a1e 0%, #0a0a0c 60%, #000 100%)" }}
                aria-hidden="true"
              >
                {it.image ? (
                  <img src={it.image} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="font-display font-semibold text-lg" style={{ color: "rgba(245,245,245,0.25)", letterSpacing: "-0.05em" }}>MT</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-display text-base truncate">{it.name}</p>
                <p className="font-mono text-[0.58rem] tracking-mt uppercase opacity-60 mt-0.5">
                  {it.category}{it.size ? ` · Size ${it.size}` : ""}
                </p>
                <div className="mt-2 flex items-center gap-2">
                  <button onClick={() => updateQty(it.id, -1, it.size)} className="mt-glass rounded-full w-7 h-7 flex items-center justify-center" aria-label="Decrease" data-testid={`cart-dec-${it.id}`}>
                    <Minus size={12} />
                  </button>
                  <span className="font-mono text-xs w-6 text-center" data-testid={`cart-qty-${it.id}`}>{it.qty}</span>
                  <button onClick={() => updateQty(it.id, 1, it.size)} className="mt-glass rounded-full w-7 h-7 flex items-center justify-center" aria-label="Increase" data-testid={`cart-inc-${it.id}`}>
                    <Plus size={12} />
                  </button>
                  <button onClick={() => removeItem(it.id, it.size)} className="ml-auto font-mono text-[0.58rem] tracking-mt uppercase opacity-50 hover:opacity-100" data-testid={`cart-remove-${it.id}`}>
                    Remove
                  </button>
                </div>
              </div>
              <div className="font-mono text-sm font-bold self-start" data-testid={`cart-price-${it.id}`}>
                {formatINR(it.qty * it.price)}
              </div>
            </div>
          ))}
        </div>

        {items.length > 0 && (
          <div className="px-7 py-6 border-t" style={{ borderColor: "var(--mt-glass-border)" }}>
            <div className="flex items-baseline justify-between mb-3">
              <span className="font-mono text-[0.65rem] tracking-mt uppercase opacity-70">Subtotal</span>
              <span className="font-display text-2xl font-medium" data-testid="cart-subtotal">{formatINR(subtotal)}</span>
            </div>
            <div className="mb-3 rounded-lg px-3 py-2 text-center font-mono text-[0.58rem] tracking-mt uppercase" style={{background:"rgba(0,170,255,0.1)",color:"#00AAFF",border:"1px solid rgba(0,170,255,0.2)"}}>✦ {shipCopy}</div>
            <span className="mt-blood-line wide mb-4" aria-hidden="true" />
            <button type="button" onClick={onCheckout} className="mt-btn mt-rainbow-edge w-full" data-testid="cart-checkout">
              Checkout <ArrowRight size={14} />
            </button>
            <button type="button" onClick={closeCart} className="block mx-auto mt-4 font-mono text-[0.62rem] tracking-mt uppercase opacity-50 hover:opacity-90" data-testid="cart-continue">
              Continue Shopping
            </button>
          </div>
        )}
      </aside>
    </>
  );
}
